import { useState, useEffect } from 'react';
import { useMedplum } from '@medplum/react';
import { Patient } from '@medplum/fhirtypes';

export interface WalkthroughState {
  status: 'completed' | 'not-started';
  version: string;
  isLoading: boolean;
  markWalkthroughCompleted: () => Promise<void>;
}

/**
 * Hook for managing the welcome walkthrough state using Patient extensions
 * Simplified to only track walkthrough completion (no individual steps)
 */
export function useWalkthroughState(): WalkthroughState {
  const medplum = useMedplum();
  const [status, setStatus] = useState<'completed' | 'not-started'>('not-started');
  const [version, setVersion] = useState<string>('1.0');
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Load walkthrough state from patient extension
  useEffect(() => {
    async function loadWalkthroughState() {
      try {
        setIsLoading(true);
        console.log('useWalkthroughState: Loading state...');
        
        const profile = medplum.getProfile();
        console.log('useWalkthroughState: Profile retrieved', { 
          hasProfile: !!profile,
          resourceType: profile?.resourceType,
          id: profile?.id
        });
        
        if (profile?.resourceType === 'Patient' && profile.id) {
          const patient = await medplum.readResource('Patient', profile.id);
          const extensions = patient.extension || [];
          
          console.log('useWalkthroughState: Patient resource loaded', { 
            id: patient.id,
            hasExtensions: !!extensions,
            extensionCount: extensions.length
          });
          
          // Get walkthrough status
          const statusExt = extensions.find(e => 
            e.url === 'https://progressnotes.app/fhir/StructureDefinition/walkthrough-status');
          
          if (statusExt?.valueString === 'completed') {
            console.log('useWalkthroughState: Walkthrough already completed');
            setStatus('completed');
          } else {
            console.log('useWalkthroughState: Walkthrough not completed, will show walkthrough');
            setStatus('not-started');
          }
          
          // Get walkthrough version
          const versionExt = extensions.find(e => 
            e.url === 'https://progressnotes.app/fhir/StructureDefinition/walkthrough-version');
          
          if (versionExt?.valueString) {
            setVersion(versionExt.valueString);
          }

          console.log('Loaded walkthrough state:', {
            status: statusExt?.valueString === 'completed' ? 'completed' : 'not-started',
            version: versionExt?.valueString || '1.0'
          });
        }
      } catch (error) {
        console.error('Error loading walkthrough state:', error);
      } finally {
        setIsLoading(false);
        console.log('useWalkthroughState: Finished loading state, isLoading set to false');
      }
    }
    
    if (medplum.isAuthenticated()) {
      console.log('useWalkthroughState: User is authenticated, loading walkthrough state');
      loadWalkthroughState();
    } else {
      console.log('useWalkthroughState: User is not authenticated, skipping state load');
      setIsLoading(false);
    }
  }, [medplum]);

  // Mark walkthrough as completed
  const markWalkthroughCompleted = async (): Promise<void> => {
    try {
      const profile = medplum.getProfile();
      if (profile?.resourceType !== 'Patient' || !profile.id) {
        throw new Error('User is not authenticated as a Patient');
      }
      
      // Optimistically update local state
      setStatus('completed');
      
      // Update server state
      const patient = await medplum.readResource('Patient', profile.id);
      const extensions = patient.extension || [];
      
      // Filter out walkthrough-related extensions
      const filteredExtensions = extensions.filter(e => 
        !e.url.startsWith('https://progressnotes.app/fhir/StructureDefinition/walkthrough-')
      );
      
      // Prepare new walkthrough extensions
      const walkthroughExtensions = [
        {
          url: 'https://progressnotes.app/fhir/StructureDefinition/walkthrough-status',
          valueString: 'completed'
        },
        {
          url: 'https://progressnotes.app/fhir/StructureDefinition/walkthrough-version',
          valueString: version
        }
      ];
      
      // Update patient resource
      await medplum.updateResource({
        ...patient,
        extension: [...filteredExtensions, ...walkthroughExtensions]
      });

      console.log('Walkthrough marked as completed');
    } catch (error) {
      console.error('Error marking walkthrough completed:', error);
      // Don't revert local state to keep UI responsive
    }
  };

  return {
    status,
    version,
    isLoading,
    markWalkthroughCompleted
  };
}